import { useState } from 'react';
import { ExpandableCostInput } from './ExpandableCostInput';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calculator } from 'lucide-react';
import {
  BUNDESLAND_CONFIG,
  estimateAdditionalCosts,
} from '@/lib/utils';
import type { Bundesland } from '@/types';

interface AdditionalCostsInputProps {
  purchasePrice: number | null;  // In Cent
  bundesland: Bundesland | null;
  values: {
    total: number | null;
    transferTax: number | null;
    notary: number | null;
    landRegistry: number | null;
    broker: number | null;
    other: number | null;
  };
  onChange: (values: AdditionalCostsInputProps['values']) => void;
  onBundeslandChange: (bundesland: Bundesland) => void;
}

export function AdditionalCostsInput({
  purchasePrice,
  bundesland,
  values,
  onChange,
  onBundeslandChange,
}: AdditionalCostsInputProps) {
  const [showDetails, setShowDetails] = useState(false);

  // Auto-Berechnung wenn Bundesland gewählt und Kaufpreis vorhanden
  const handleAutoCalculate = () => {
    if (!purchasePrice || !bundesland) return;

    const estimated = estimateAdditionalCosts(purchasePrice, bundesland, true);

    onChange({
      total: estimated.total,
      transferTax: estimated.transferTax,
      notary: estimated.notary,
      landRegistry: estimated.landRegistry,
      broker: estimated.broker,
      other: 0,
    });
    setShowDetails(true);
  };

  const items = [
    { key: 'transferTax', label: `Grunderwerbsteuer${bundesland ? ` (${BUNDESLAND_CONFIG[bundesland]?.transferTaxRate}%)` : ''}`, value: values.transferTax },
    { key: 'notary', label: 'Notar (~1,5%)', value: values.notary },
    { key: 'landRegistry', label: 'Grundbuch (~0,5%)', value: values.landRegistry },
    { key: 'broker', label: 'Makler (~3,57%)', value: values.broker },
    { key: 'other', label: 'Sonstiges', value: values.other },
  ];

  const handleItemChange = (key: string, value: number | null) => {
    onChange({
      ...values,
      [key]: value,
      total: null, // Reset total wenn Details geändert werden
    });
  };

  return (
    <div className="space-y-3">
      {/* Bundesland Auswahl */}
      <div className="space-y-1">
        <Label className="text-xs text-foreground-muted">Bundesland</Label>
        <div className="flex gap-2">
          <select
            value={bundesland || ''}
            onChange={(e) => onBundeslandChange(e.target.value as Bundesland)}
            className="input flex-1 h-9 text-sm bg-background border border-border rounded-md px-3"
          >
            <option value="">Bitte wählen...</option>
            {Object.entries(BUNDESLAND_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>
                {config.label} ({config.transferTaxRate}%)
              </option>
            ))}
          </select>
          {bundesland && purchasePrice && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleAutoCalculate}
              className="h-9"
            >
              <Calculator className="w-4 h-4 mr-1" />
              Berechnen
            </Button>
          )}
        </div>
      </div>

      {/* Kosten-Eingabe */}
      <ExpandableCostInput
        label="Kaufnebenkosten"
        totalValue={values.total}
        onTotalChange={(v) => onChange({ ...values, total: v })}
        items={items}
        onItemChange={handleItemChange}
        showDetails={showDetails}
        onToggleDetails={setShowDetails}
      />
    </div>
  );
}
